import { memo } from 'react';
import { LABEL_STYLE } from '../shared/constants';
import { InnovationTooltip } from '../shared/InnovationTooltip';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useInnovationData } from './useInnovationData';

const COLORS = {
  'Innovation development': '#8b5cf6',
  'Innovation use': '#22c55e',
  'Innovation Package': '#ec4899',
};

const SHORT_LABELS = {
  'Innovation development': 'Development',
  'Innovation use': 'Use',
  'Innovation Package': 'Package',
};

export const InnovationTypeBreakdownChart = memo(function InnovationTypeBreakdownChart() {
  const { typeBreakdown, totalCount, innovationResults } = useInnovationData();
  const yAxisWidth = useResponsiveYAxisWidth(90);
  if (!typeBreakdown || totalCount === 0) return (
    <ChartWrapper title="Innovation Results by Type">
      <NoChartData message="No innovation results in the current selection" detail="This chart shows the split of Innovation Development, Use, and Package results. The current program/filter selection contains no innovation-type results." />
    </ChartWrapper>
  );

  const data = Object.entries(typeBreakdown)
    .filter(([, count]) => count > 0)
    .map(([type, count]) => ({
      name: SHORT_LABELS[type] || type,
      fullName: type,
      count,
      percentage: ((count / totalCount) * 100).toFixed(1),
    }));

  const exportData = data.map(d => ({ Type: d.fullName, Count: d.count, Percentage: d.percentage + '%' }));

  return (
    <ChartWrapper title="Innovation Results by Type" info="Split of innovation results by type: Development (new innovations), Use (adoption), and Packages (bundled innovations)." chartData={exportData} exportFilename="innovation-type-breakdown" sourceResults={innovationResults}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart margin={{ top: 5, right: 40, bottom: 5, left: 40 }}>
              <Pie data={data} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={40} paddingAngle={2} animationBegin={0} animationDuration={400} label={({ name, percentage }) => `${name} (${percentage}%)`}>
                {data.map((entry) => (
                  <Cell key={entry.fullName} fill={COLORS[entry.fullName] || '#6b7280'} />
                ))}
              </Pie>
              <Tooltip content={<InnovationTooltip unit="innovations" />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: yAxisWidth < 90 ? 9 : 12 }} width={yAxisWidth} />
              <Tooltip content={<InnovationTooltip unit="innovations" />} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} animationBegin={0} animationDuration={400}>
                {data.map((entry) => (
                  <Cell key={entry.fullName} fill={COLORS[entry.fullName] || '#6b7280'} />
                ))}
                <LabelList dataKey="count" position="right" style={LABEL_STYLE} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="mt-3 flex justify-center gap-6 text-sm text-gray-600">
        {data.map(d => (
          <div key={d.fullName} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[d.fullName] }} />
            <span>{d.fullName}: <strong>{d.count}</strong></span>
          </div>
        ))}
      </div>
    </ChartWrapper>
  );
});
